import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

// ==================== EMPLOYEES ====================
export interface EmployeeListItem {
  id: string;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  corporateEmail: string;
  photoUrl?: string;
  status: string;
  hireDate: string;
  position: { id: string; title: string };
  department: { id: string; name: string };
  country: { id: string; code: string; nameEs: string };
  contractType: { id: string; name: string };
}

export function useEmployees(params: { search?: string; countryId?: string; departmentId?: string; status?: string; page?: number; pageSize?: number } = {}) {
  return useQuery({
    queryKey: ['employees', params],
    queryFn: async () => {
      const { data } = await api.get('/employees', { params });
      return data as { items: EmployeeListItem[]; total: number; page: number; pageSize: number };
    },
  });
}

export function useEmployee(id?: string) {
  return useQuery({
    queryKey: ['employee', id],
    enabled: !!id,
    queryFn: async () => (await api.get(`/employees/${id}`)).data,
  });
}

export function useOrgChart(countryId?: string) {
  return useQuery({
    queryKey: ['org-chart', countryId],
    queryFn: async () => (await api.get('/employees/org-chart', { params: { countryId } })).data,
  });
}

export function useCreateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => (await api.post('/employees', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });
}

export function useUpdateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: any }) =>
      (await api.put(`/employees/${id}`, payload)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['employee', vars.id] });
    },
  });
}

export function useDeleteEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/employees/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });
}

// ==================== LEAVE REQUESTS ====================
export interface LeaveRequest {
  id: string;
  employeeId: string;
  type: string;
  startDate: string;
  endDate: string;
  workingDays: number;
  reason?: string;
  status: string;
  exceedsSuggestedLimit: boolean;
  createdAt: string;
  employee: { id: string; firstName: string; lastName: string; photoUrl?: string };
}

export function useLeaveRequests(params: { status?: string; type?: string; employeeId?: string } = {}) {
  return useQuery({
    queryKey: ['leave-requests', params],
    queryFn: async () => (await api.get('/leave-requests', { params })).data as { items: LeaveRequest[]; total: number },
  });
}

export function usePendingLeaves() {
  return useQuery({
    queryKey: ['leave-requests', 'pending'],
    queryFn: async () => (await api.get('/leave-requests/pending')).data as { items: LeaveRequest[] },
  });
}

export function useMyBalance() {
  return useQuery({
    queryKey: ['leave-requests', 'my-balance'],
    queryFn: async () => (await api.get('/leave-requests/my-balance')).data,
  });
}

export function useCreateLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => (await api.post('/leave-requests', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leave-requests'] }),
  });
}

export function useApproveLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.put(`/leave-requests/${id}/approve`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leave-requests'] }),
  });
}

export function useRejectLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      (await api.put(`/leave-requests/${id}/reject`, { reason })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leave-requests'] }),
  });
}

export function useCancelLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.put(`/leave-requests/${id}/cancel`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leave-requests'] }),
  });
}

// ==================== CALENDAR ====================
export function useCalendar(from: string, to: string, countryId?: string) {
  return useQuery({
    queryKey: ['calendar', from, to, countryId],
    queryFn: async () => (await api.get('/calendar', { params: { from, to, countryId } })).data,
  });
}

// ==================== CATALOGS ====================
export function useCountries() {
  return useQuery({
    queryKey: ['catalogs', 'countries'],
    staleTime: 5 * 60_000,
    queryFn: async () => (await api.get('/catalogs/countries')).data as { items: any[] },
  });
}

export function useDepartments() {
  return useQuery({
    queryKey: ['catalogs', 'departments'],
    staleTime: 5 * 60_000,
    queryFn: async () => (await api.get('/catalogs/departments')).data as { items: any[] },
  });
}

export function usePositions() {
  return useQuery({
    queryKey: ['catalogs', 'positions'],
    staleTime: 5 * 60_000,
    queryFn: async () => (await api.get('/catalogs/positions')).data as { items: any[] },
  });
}

export function useContractTypes(countryId?: string) {
  return useQuery({
    queryKey: ['catalogs', 'contract-types', countryId],
    staleTime: 5 * 60_000,
    queryFn: async () => (await api.get('/catalogs/contract-types', { params: { countryId } })).data as { items: any[] },
  });
}

export function useHolidays(countryId?: string, year?: number) {
  return useQuery({
    queryKey: ['catalogs', 'holidays', countryId, year],
    queryFn: async () => (await api.get('/catalogs/holidays', { params: { countryId, year } })).data as { items: any[] },
  });
}

export function useCreateHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => (await api.post('/catalogs/holidays', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalogs', 'holidays'] }),
  });
}

export function useUpdateHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: any }) =>
      (await api.put(`/catalogs/holidays/${id}`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalogs', 'holidays'] }),
  });
}

export function useDeleteHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/catalogs/holidays/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalogs', 'holidays'] }),
  });
}

// ==================== CATALOG CRUD ====================
export function useCreateDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => (await api.post('/catalogs/departments', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalogs', 'departments'] }),
  });
}

export function useDeleteDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/catalogs/departments/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalogs', 'departments'] }),
  });
}

export function useCreatePosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => (await api.post('/catalogs/positions', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalogs', 'positions'] }),
  });
}

export function useDeletePosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/catalogs/positions/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalogs', 'positions'] }),
  });
}

// ==================== USERS ====================
export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: async () => (await api.get('/users')).data as { items: any[] },
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => (await api.post('/users', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

// ==================== NOTIFICATIONS ====================
export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    refetchInterval: 60_000,
    queryFn: async () => (await api.get('/notifications')).data as { items: any[]; unreadCount: number },
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.put(`/notifications/${id}/read`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.put('/notifications/mark-all-read')).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

// ==================== AUDIT ====================
export function useAuditLog(params: { module?: string; action?: string; page?: number; pageSize?: number } = {}) {
  return useQuery({
    queryKey: ['audit-log', params],
    queryFn: async () => (await api.get('/audit-log', { params })).data,
  });
}

// ==================== DASHBOARD ====================
export function useDashboardStats() {
  return useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: async () => (await api.get('/dashboard/stats')).data,
  });
}

// ==================== DOCUMENTS ====================
export function useEmployeeDocuments(employeeId?: string) {
  return useQuery({
    queryKey: ['documents', employeeId],
    enabled: !!employeeId,
    queryFn: async () => (await api.get(`/employees/${employeeId}/documents`)).data as { items: any[] },
  });
}

export function useInitDocumentUpload() {
  return useMutation({
    mutationFn: async ({ employeeId, payload }: { employeeId: string; payload: any }) =>
      (await api.post(`/employees/${employeeId}/documents/init-upload`, payload)).data as { document: any; uploadUrl: string },
  });
}

export function useUpdateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: any }) =>
      (await api.put(`/documents/${id}`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/documents/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

export function useDocumentDownloadUrl() {
  return useMutation({
    mutationFn: async ({ id, inline }: { id: string; inline?: boolean }) =>
      (await api.get(`/documents/${id}/download`, { params: inline ? { inline: 1 } : {} })).data as { url: string },
  });
}

export function useConfirmDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status?: string }) =>
      (await api.put(`/documents/${id}/confirm`, { status: status ?? 'VALID' })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

// ==================== PHOTO ====================
export function useInitPhotoUpload() {
  return useMutation({
    mutationFn: async ({ employeeId, payload }: { employeeId: string; payload: any }) =>
      (await api.post(`/employees/${employeeId}/photo/init-upload`, payload)).data as { uploadUrl: string; s3Key: string },
  });
}

export function useConfirmPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ employeeId, s3Key }: { employeeId: string; s3Key: string }) =>
      (await api.put(`/employees/${employeeId}/photo`, { s3Key })).data as { photoUrl: string },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['employee', vars.employeeId] });
      qc.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

// ==================== EVALUATIONS ====================
export function useEvaluations(employeeId?: string) {
  return useQuery({
    queryKey: ['evaluations', employeeId],
    enabled: !!employeeId,
    queryFn: async () => (await api.get(`/employees/${employeeId}/evaluations`)).data as { items: any[] },
  });
}

export function useCreateEvaluation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ employeeId, payload }: { employeeId: string; payload: any }) =>
      (await api.post(`/employees/${employeeId}/evaluations`, payload)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['evaluations', vars.employeeId] });
      qc.invalidateQueries({ queryKey: ['employee', vars.employeeId] });
    },
  });
}

export function useUpdateEvaluation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: any }) =>
      (await api.put(`/evaluations/${id}`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['evaluations'] }),
  });
}

export function useDeleteEvaluation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/evaluations/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['evaluations'] }),
  });
}

// ==================== TRAININGS ====================
export function useTrainings(employeeId?: string) {
  return useQuery({
    queryKey: ['trainings', employeeId],
    enabled: !!employeeId,
    queryFn: async () => (await api.get(`/employees/${employeeId}/trainings`)).data as { items: any[] },
  });
}

export function useCreateTraining() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ employeeId, payload }: { employeeId: string; payload: any }) =>
      (await api.post(`/employees/${employeeId}/trainings`, payload)).data,
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['trainings', vars.employeeId] }),
  });
}

export function useDeleteTraining() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/trainings/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['trainings'] }),
  });
}

// ==================== COMPLIANCE ====================
export function useUpdateCompliance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ employeeId, payload }: { employeeId: string; payload: any }) =>
      (await api.put(`/employees/${employeeId}/compliance`, payload)).data,
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['employee', vars.employeeId] }),
  });
}

// ==================== POLICIES ====================
export function usePolicies() {
  return useQuery({
    queryKey: ['policies'],
    queryFn: async () => (await api.get('/policies')).data as { items: any[] },
  });
}

export function useUpsertPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => (await api.post('/policies', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['policies'] }),
  });
}

export function useDeletePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (key: string) => (await api.delete(`/policies/${key}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['policies'] }),
  });
}

export function useUpdateUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, role }: { id: string; role: string }) =>
      (await api.put(`/users/${id}/role`, { role })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post(`/users/${id}/reset-password`)).data as {
        userId: string;
        email: string;
        password: string;
        notice: string;
      },
  });
}

/**
 * Reinicia (o crea) la contraseña de un colaborador por su employeeId.
 * Si el colaborador no tiene cuenta de usuario, el backend se la crea.
 */
export function useResetEmployeePassword() {
  return useMutation({
    mutationFn: async (employeeId: string) =>
      (await api.post(`/employees/${employeeId}/reset-password`)).data as {
        userId: string;
        email: string;
        password: string;
        created: boolean;
        notice: string;
      },
  });
}

// ==================== EMPLOYEE RECORDS (premios / llamados de atención) ====================
export interface EmployeeRecord {
  id: string;
  employeeId: string;
  type: 'AWARD' | 'WARNING';
  title: string;
  description?: string | null;
  severity?: 'MINOR' | 'MODERATE' | 'SEVERE' | null;
  points?: number | null;
  occurredOn: string;
  createdAt: string;
}

export function useEmployeeRecords(employeeId?: string) {
  return useQuery({
    queryKey: ['employee-records', employeeId],
    enabled: !!employeeId,
    queryFn: async () =>
      (await api.get(`/employees/${employeeId}/records`)).data as { items: EmployeeRecord[] },
  });
}

export function useCreateRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ employeeId, payload }: { employeeId: string; payload: any }) =>
      (await api.post(`/employees/${employeeId}/records`, payload)).data,
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: ['employee-records', vars.employeeId] }),
  });
}

export function useDeleteRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ recordId }: { recordId: string; employeeId: string }) =>
      (await api.delete(`/records/${recordId}`)).data,
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: ['employee-records', vars.employeeId] }),
  });
}

export interface MyRecordsSummary {
  awards: EmployeeRecord[];
  warnings: EmployeeRecord[];
  awardCount: number;
  warningCount: number;
  totalPoints: number;
}

export function useMyRecords() {
  return useQuery({
    queryKey: ['my-records'],
    queryFn: async () => (await api.get('/me/records')).data as MyRecordsSummary,
  });
}

/** Perfil del usuario actual (incluye su colaborador con país/departamento/puesto). */
export function useMe() {
  return useQuery({
    queryKey: ['me-profile'],
    queryFn: async () => (await api.get('/auth/me')).data as any,
  });
}

// ==================== ANNOUNCEMENTS ====================
export interface AnnouncementAttachment {
  s3Key: string;
  url: string;
  name: string;
  size: number;
  mimeType: string;
  kind: 'image' | 'file';
}

export interface AnnouncementListItem {
  id: string;
  title: string;
  body: string;
  status: string;
  pinned: boolean;
  audienceType: string;
  audienceCountryIds: string[];
  audienceDepartmentIds: string[];
  attachmentName?: string;
  attachmentSize?: number;
  attachments?: AnnouncementAttachment[];
  createdBy: {
    id: string;
    email: string;
    employee?: { firstName: string; lastName: string; photoUrl?: string; position?: { title: string } };
  };
  isRead: boolean;
  readCount: number;
  totalAudience: number;
  publishedAt?: string;
  createdAt: string;
}

export function useAnnouncements(params: { status?: string; search?: string; page?: number; pageSize?: number } = {}) {
  return useQuery({
    queryKey: ['announcements', params],
    queryFn: async () => (await api.get('/announcements', { params })).data as { items: AnnouncementListItem[]; total: number; page: number; pageSize: number },
  });
}

export function useAnnouncement(id?: string) {
  return useQuery({
    queryKey: ['announcement', id],
    enabled: !!id,
    queryFn: async () => (await api.get(`/announcements/${id}`)).data,
  });
}

export function useAnnouncementUnreadCount() {
  return useQuery({
    queryKey: ['announcements', 'unread-count'],
    refetchInterval: 60_000,
    queryFn: async () => (await api.get('/announcements/unread-count')).data as { count: number },
  });
}

export function useCreateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => (await api.post('/announcements', payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['announcements'] });
    },
  });
}

export function useUpdateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: any }) =>
      (await api.put(`/announcements/${id}`, payload)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['announcements'] });
      qc.invalidateQueries({ queryKey: ['announcement', vars.id] });
    },
  });
}

export function useDeleteAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/announcements/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements'] }),
  });
}

export function useInitAnnouncementAttachment() {
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: { fileName: string; mimeType: string; size: number } }) =>
      (await api.post(`/announcements/${id}/attachment`, payload)).data as { uploadUrl: string; s3Key: string },
  });
}

export function useAnnouncementAttachmentUrl() {
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.get(`/announcements/${id}/attachment`)).data as { url: string },
  });
}

/** Inicia la subida de UN adjunto de la galería (devuelve presigned PUT url). */
export function useAddAnnouncementMedia() {
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: { fileName: string; mimeType: string; size: number };
    }) =>
      (await api.post(`/announcements/${id}/media`, payload)).data as {
        uploadUrl: string;
        s3Key: string;
        item: AnnouncementAttachment;
      },
  });
}

/** Elimina un adjunto de la galería por su s3Key. */
export function useDeleteAnnouncementMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, s3Key }: { id: string; s3Key: string }) =>
      (await api.delete(`/announcements/${id}/media`, { data: { s3Key } })).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['announcement', vars.id] });
      qc.invalidateQueries({ queryKey: ['announcements'] });
    },
  });
}

// ==================== AUTH EXTRAS ====================
export function useChangePassword() {
  return useMutation({
    mutationFn: async (payload: { currentPassword: string; newPassword: string }) =>
      (await api.post('/auth/change-password', payload)).data,
  });
}

// ==================== QUINIELA MUNDIAL 2026 ====================

export type WcPhase = 'GROUP' | 'R32' | 'R16' | 'QF' | 'SF' | 'THIRD' | 'FINAL';

export interface WcTeam {
  id: string;
  code: string;
  name: string;
  nameEs: string;
  groupLetter: string;
  iso2: string;
  eliminated: boolean;
}
export interface WcPlayer {
  id: string;
  number: number;
  position: 'GK' | 'DEF' | 'MID' | 'FWD';
  name: string;
  jersey: string;
  club?: string;
  goals: number;
}
export interface WcTeamRef {
  id: string;
  code: string;
  nameEs: string;
  iso2: string;
}
export type WcSlot =
  | { type: 'GROUP_POS'; pos: number; group: string }
  | { type: 'WINNER'; ref: number }
  | { type: 'LOSER'; ref: number }
  | { type: 'THIRD_SET'; groups: string[] };

export interface WcMatch {
  id: string;
  number: number;
  phase: WcPhase;
  group: string | null;
  city: string;
  kickoff: string;
  status: 'SCHEDULED' | 'LIVE' | 'FINISHED';
  locked: boolean;
  homeTeam: WcTeamRef | null;
  awayTeam: WcTeamRef | null;
  homeSlot: WcSlot | null;
  awaySlot: WcSlot | null;
  homeScore: number | null;
  awayScore: number | null;
  reg90Home: number | null; // marcador al 90' cuando hubo alargue (contra el que se califica)
  reg90Away: number | null;
  homePens: number | null;
  awayPens: number | null;
  liveMinute: number | null;
  liveStatus: string | null;
  scorerIds: string[];
  myPrediction: {
    homeScore: number;
    awayScore: number;
    scorerId: string | null;
    scorerJersey: string | null;
    points: number;
    scoredAt: string | null;
  } | null;
}

export interface WcPlayerSearchResult {
  id: string;
  number: number;
  position: string;
  jersey: string;
  name: string;
  team: WcTeamRef;
}

export interface WcScoringConfig {
  id: number;
  pointsExact: number;
  pointsOutcomeGd: number;
  pointsOutcome: number;
  pointsTeamGoals: number;
  pointsScorer: number;
  multGroup: number;
  multR32: number;
  multR16: number;
  multQF: number;
  multSF: number;
  multFinal: number;
  pointsChampion: number;
  pointsFinalist: number;
  pointsGoldenBoot: number;
  lockMinutesBefore: number;
  reminderMinutesBefore: number;
}

export interface WcOverview {
  myPoints: number;
  myPredictions: number;
  myExact: number;
  myRank: number | null;
  participants: number;
  totalMatches: number;
  finishedMatches: number;
  upcoming: Array<{
    id: string;
    number: number;
    phase: WcPhase;
    group: string | null;
    kickoff: string;
    city: string;
    locked: boolean;
    homeTeam: WcTeamRef | null;
    awayTeam: WcTeamRef | null;
    homeSlot: WcSlot | null;
    awaySlot: WcSlot | null;
    hasPrediction: boolean;
  }>;
}

export interface WcLeaderRow {
  rank: number;
  userId: string;
  name: string;
  photoUrl: string | null;
  country: { code: string; nameEs: string } | null;
  matchPoints: number;
  bonusPoints: number;
  totalPoints: number;
  predictions: number;
  exactCount: number;
}

export interface WcBonusPick {
  id: string;
  championTeamId: string | null;
  runnerUpTeamId: string | null;
  goldenBootPlayerId: string | null;
  points: number;
}

export function useWcOverview() {
  return useQuery({
    queryKey: ['wc-overview'],
    queryFn: async () => (await api.get('/quiniela/overview')).data as WcOverview,
  });
}
export function useWcScoring() {
  return useQuery({
    queryKey: ['wc-scoring'],
    queryFn: async () => (await api.get('/quiniela/scoring')).data as WcScoringConfig,
  });
}
export function useWcTeams() {
  return useQuery({
    queryKey: ['wc-teams'],
    queryFn: async () => (await api.get('/quiniela/teams')).data as { items: WcTeam[] },
    staleTime: 5 * 60_000,
  });
}
export function useWcTeamPlayers(teamId?: string) {
  return useQuery({
    queryKey: ['wc-players', teamId],
    enabled: !!teamId,
    staleTime: 5 * 60_000,
    queryFn: async () =>
      (await api.get(`/quiniela/teams/${teamId}/players`)).data as { items: WcPlayer[] },
  });
}
export function useWcMatches(params: { phase?: string; group?: string } = {}) {
  return useQuery({
    queryKey: ['wc-matches', params],
    queryFn: async () =>
      (await api.get('/quiniela/matches', { params })).data as { items: WcMatch[] },
    // Mientras haya un partido EN VIVO, refresca el marcador cada 30s.
    refetchInterval: (q: any) =>
      q.state.data?.items?.some((m: WcMatch) => m.status === 'LIVE') ? 30_000 : false,
  });
}
export function useWcSaveLive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: {
      matchId: string;
      homeScore: number;
      awayScore: number;
      liveMinute?: number | null;
      clear?: boolean;
    }) =>
      (
        await api.put(`/quiniela/admin/matches/${p.matchId}/live`, {
          homeScore: p.homeScore,
          awayScore: p.awayScore,
          liveMinute: p.liveMinute ?? null,
          clear: p.clear ?? false,
        })
      ).data,
    onSettled: () => qc.invalidateQueries({ queryKey: ['wc-matches'] }),
  });
}
export function useWcSavePrediction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: {
      matchId: string;
      homeScore: number;
      awayScore: number;
      scorerId?: string | null;
    }) =>
      (
        await api.put(`/quiniela/matches/${p.matchId}/prediction`, {
          homeScore: p.homeScore,
          awayScore: p.awayScore,
          scorerId: p.scorerId ?? null,
        })
      ).data,
    // Refresca también ante error: si el partido se cerró (409), la tarjeta
    // se actualiza al estado bloqueado en vez de quedar editable de forma engañosa.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['wc-matches'] });
      qc.invalidateQueries({ queryKey: ['wc-overview'] });
    },
  });
}
export function useWcMyPredictions() {
  return useQuery({
    queryKey: ['wc-my-predictions'],
    queryFn: async () => (await api.get('/quiniela/me/predictions')).data,
  });
}
export function useWcLeaderboard() {
  return useQuery({
    queryKey: ['wc-leaderboard'],
    queryFn: async () =>
      (await api.get('/quiniela/leaderboard')).data as { items: WcLeaderRow[]; total: number },
  });
}

export interface WcBreakdownMatch {
  number: number;
  phase: WcPhase;
  homeTeam: { nameEs: string; iso2: string | null };
  awayTeam: { nameEs: string; iso2: string | null };
  result: { home: number; away: number };
  scoredAgainst: { home: number; away: number } | null; // marcador al 90' si hubo alargue
  prediction: { home: number; away: number; scorerJersey: string | null; scorerHit: boolean };
  multiplier: number;
  points: number;
  reasons: { label: string; pts: number }[];
}
export interface WcBreakdown {
  userId: string;
  name: string;
  photoUrl: string | null;
  country: { code: string; nameEs: string } | null;
  matchPoints: number;
  bonusPoints: number;
  totalPoints: number;
  isSelf: boolean;
  matches: WcBreakdownMatch[];
  bonus: {
    points: number;
    revealed: boolean;
    champion: { nameEs: string; iso2: string | null } | null;
    runnerUp: { nameEs: string; iso2: string | null } | null;
    goldenBoot: { jersey: string; team: { nameEs: string; iso2: string | null } } | null;
    breakdown: { champion: boolean; finalist: boolean; goldenBoot: boolean } | null;
  };
}
export function useWcBreakdown(userId: string | null) {
  return useQuery({
    queryKey: ['wc-breakdown', userId],
    enabled: !!userId,
    queryFn: async () => (await api.get(`/quiniela/breakdown/${userId}`)).data as WcBreakdown,
  });
}
export function useWcBonus() {
  return useQuery({
    queryKey: ['wc-bonus'],
    queryFn: async () =>
      (await api.get('/quiniela/bonus')).data as {
        pick: WcBonusPick | null;
        locked: boolean;
        deadline: string | null; // fecha límite (inicio de octavos)
        championTeam: WcTeamRef | null;
        runnerUpTeam: WcTeamRef | null;
        goldenBootPlayer: WcPlayerSearchResult | null;
      },
  });
}
export function useWcPlayerSearch(q: string) {
  return useQuery({
    queryKey: ['wc-player-search', q],
    enabled: q.trim().length >= 2,
    staleTime: 5 * 60_000,
    queryFn: async () =>
      (await api.get('/quiniela/players', { params: { q: q.trim() } })).data as {
        items: WcPlayerSearchResult[];
      },
  });
}
export function useWcSaveBonus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: {
      championTeamId?: string | null;
      runnerUpTeamId?: string | null;
      goldenBootPlayerId?: string | null;
    }) => (await api.put('/quiniela/bonus', p)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wc-bonus'] }),
  });
}

// ── Admin ──
export function useWcSaveResult() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: {
      matchId: string;
      homeScore: number;
      awayScore: number;
      homePens?: number | null;
      awayPens?: number | null;
      scorerIds?: string[];
    }) =>
      (
        await api.put(`/quiniela/admin/matches/${p.matchId}/result`, {
          homeScore: p.homeScore,
          awayScore: p.awayScore,
          homePens: p.homePens ?? null,
          awayPens: p.awayPens ?? null,
          scorerIds: p.scorerIds ?? [],
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wc-matches'] });
      qc.invalidateQueries({ queryKey: ['wc-leaderboard'] });
      qc.invalidateQueries({ queryKey: ['wc-overview'] });
    },
  });
}
export function useWcOverrideTeams() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { matchId: string; homeTeamId: string | null; awayTeamId: string | null }) =>
      (await api.put(`/quiniela/admin/matches/${p.matchId}/teams`, p)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wc-matches'] }),
  });
}
export function useWcSaveScoring() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cfg: Partial<WcScoringConfig>) =>
      (await api.put('/quiniela/admin/scoring', cfg)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wc-scoring'] });
      qc.invalidateQueries({ queryKey: ['wc-matches'] });
    },
  });
}
export function useWcRecompute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post('/quiniela/admin/recompute', {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wc-leaderboard'] });
      qc.invalidateQueries({ queryKey: ['wc-matches'] });
      qc.invalidateQueries({ queryKey: ['wc-overview'] });
    },
  });
}
export function useWcNotify() {
  return useMutation({
    mutationFn: async (p: { title: string; body: string; url?: string }) =>
      (await api.post('/quiniela/admin/notify', p)).data,
  });
}

// ==================== DIFUSIONES DE CORREO ====================

export interface BroadcastListItem {
  id: string;
  subject: string;
  templateKey: string | null;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  failures: Array<{ email: string; error: string }> | null;
  status: 'SENDING' | 'COMPLETED' | 'COMPLETED_WITH_ERRORS';
  createdAt: string;
}

export function useBroadcastStatus() {
  return useQuery({
    queryKey: ['broadcast-status'],
    queryFn: async () =>
      (await api.get('/broadcasts/status')).data as { configured: boolean; sender: string | null },
    staleTime: 5 * 60_000,
  });
}
export function useBroadcasts() {
  return useQuery({
    queryKey: ['broadcasts'],
    queryFn: async () => (await api.get('/broadcasts')).data as { items: BroadcastListItem[] },
  });
}
export function useCreateBroadcast() {
  return useMutation({
    mutationFn: async (p: {
      subject: string;
      templateKey?: string | null;
      bodyHtml: string;
      columns: string[];
      totalRecipients: number;
    }) => (await api.post('/broadcasts', p)).data as { id: string },
  });
}
export function useSendBroadcastBatch() {
  return useMutation({
    mutationFn: async (p: { id: string; rows: Record<string, string>[] }) =>
      (await api.post(`/broadcasts/${p.id}/send-batch`, { rows: p.rows })).data as {
        results: Array<{ email: string; ok: boolean; error?: string }>;
        sentCount: number;
        failedCount: number;
        done: boolean;
      },
  });
}
export function useSendBroadcastTest() {
  return useMutation({
    mutationFn: async (p: {
      to: string;
      subject: string;
      bodyHtml: string;
      columns: string[];
      sampleRow?: Record<string, string>;
    }) => (await api.post('/broadcasts/test', p)).data,
  });
}
